import React from 'react';
import { Shield, Target, Award, Lock, FileCheck, Users } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const About = () => {
  return (
    <div className="dark-container min-h-screen pt-28 pb-20">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Hero Section */}
        <div className="text-center mb-20">
          <h1 className="display-large text-white mb-6">About MatrixLaunch</h1>
          <p className="body-large text-white/70 max-w-3xl mx-auto leading-relaxed">
            We're building the future of decentralized fundraising, connecting visionary blockchain projects 
            with a global community of crypto investors.
          </p>
        </div>

        {/* Vision & Mission */}
        <div className="grid md:grid-cols-2 gap-8 mb-20">
          <Card className="bg-white/5 border border-white/25 backdrop-blur-xl">
            <CardContent className="p-8">
              <div className="w-16 h-16 mb-6 bg-brand-primary/10 border border-brand-primary rounded-sm flex items-center justify-center">
                <Target className="text-brand-primary" size={32} />
              </div>
              <h2 className="heading-1 text-white mb-4">Our Vision</h2>
              <p className="text-white/70 leading-relaxed">
                To become the world's most trusted decentralized launchpad, empowering innovative blockchain 
                projects to reach their full potential while providing investors with secure, transparent, 
                and profitable investment opportunities.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-white/5 border border-white/25 backdrop-blur-xl">
            <CardContent className="p-8">
              <div className="w-16 h-16 mb-6 bg-brand-primary/10 border border-brand-primary rounded-sm flex items-center justify-center">
                <Award className="text-brand-primary" size={32} />
              </div>
              <h2 className="heading-1 text-white mb-4">Our Mission</h2>
              <p className="text-white/70 leading-relaxed">
                Democratize access to early-stage blockchain investments through rigorous project vetting, 
                community-driven growth, and a revolutionary multi-level referral system that rewards network builders.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Why Choose Us */}
        <div className="mb-20">
          <h2 className="display-medium text-white text-center mb-12">Why Choose MatrixLaunch?</h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-white/5 border border-white/25 backdrop-blur-xl hover:border-brand-primary transition-all duration-300">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 mx-auto mb-6 bg-brand-primary/10 border border-brand-primary rounded-sm flex items-center justify-center">
                  <Shield className="text-brand-primary" size={28} />
                </div>
                <h3 className="heading-2 text-white mb-3">Rigorous Vetting</h3>
                <p className="text-white/60 text-sm leading-relaxed">
                  Every project undergoes comprehensive due diligence including team verification, 
                  smart contract audits, and tokenomics analysis.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white/5 border border-white/25 backdrop-blur-xl hover:border-brand-primary transition-all duration-300">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 mx-auto mb-6 bg-brand-primary/10 border border-brand-primary rounded-sm flex items-center justify-center">
                  <Lock className="text-brand-primary" size={28} />
                </div>
                <h3 className="heading-2 text-white mb-3">Secure Platform</h3>
                <p className="text-white/60 text-sm leading-relaxed">
                  Built on blockchain technology with smart contract protection, 
                  multi-signature wallets, and industry-leading security protocols.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white/5 border border-white/25 backdrop-blur-xl hover:border-brand-primary transition-all duration-300">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 mx-auto mb-6 bg-brand-primary/10 border border-brand-primary rounded-sm flex items-center justify-center">
                  <Users className="text-brand-primary" size={28} />
                </div>
                <h3 className="heading-2 text-white mb-3">Community First</h3>
                <p className="text-white/60 text-sm leading-relaxed">
                  Join a thriving global network of investors and entrepreneurs. 
                  Earn passive income through our multi-level referral program.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Security & Transparency */}
        <div className="bg-white/5 border border-brand-primary rounded-sm p-12 backdrop-blur-xl mb-20">
          <div className="text-center mb-12">
            <h2 className="display-medium text-white mb-4">Security & Transparency</h2>
            <p className="body-medium text-white/70 max-w-3xl mx-auto">
              Your trust is our priority. We maintain the highest standards of security and transparency.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-brand-primary/10 border border-brand-primary rounded-sm flex items-center justify-center">
                  <FileCheck className="text-brand-primary" size={24} />
                </div>
              </div>
              <div>
                <h3 className="text-white font-semibold mb-2">Smart Contract Audits</h3>
                <p className="text-white/60 text-sm">
                  All projects are audited by leading blockchain security firms including CertiK, 
                  Hacken, and Trail of Bits before launch.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-brand-primary/10 border border-brand-primary rounded-sm flex items-center justify-center">
                  <Shield className="text-brand-primary" size={24} />
                </div>
              </div>
              <div>
                <h3 className="text-white font-semibold mb-2">KYC Verification</h3>
                <p className="text-white/60 text-sm">
                  Mandatory KYC for all project teams ensures accountability and builds investor confidence. 
                  User KYC available for enhanced security.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-brand-primary/10 border border-brand-primary rounded-sm flex items-center justify-center">
                  <Lock className="text-brand-primary" size={24} />
                </div>
              </div>
              <div>
                <h3 className="text-white font-semibold mb-2">Fund Security</h3>
                <p className="text-white/60 text-sm">
                  Investor funds are protected by multi-signature smart contracts with time-locked 
                  release schedules tied to project milestones.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-brand-primary/10 border border-brand-primary rounded-sm flex items-center justify-center">
                  <Users className="text-brand-primary" size={24} />
                </div>
              </div>
              <div>
                <h3 className="text-white font-semibold mb-2">Community Governance</h3>
                <p className="text-white/60 text-sm">
                  Our token holders participate in platform decisions through decentralized governance, 
                  ensuring the community's voice is heard.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid md:grid-cols-4 gap-8 text-center">
          <div>
            <div className="text-4xl font-bold text-brand-primary mb-2">89</div>
            <div className="text-white/60">Projects Launched</div>
          </div>
          <div>
            <div className="text-4xl font-bold text-brand-primary mb-2">128K+</div>
            <div className="text-white/60">Active Investors</div>
          </div>
          <div>
            <div className="text-4xl font-bold text-brand-primary mb-2">$47.5M</div>
            <div className="text-white/60">Total Raised</div>
          </div>
          <div>
            <div className="text-4xl font-bold text-brand-primary mb-2">95%</div>
            <div className="text-white/60">Success Rate</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
